import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MapPin, Search, Globe, Clock } from 'lucide-react';
import { getCityOptions, getTimezoneDisplayName } from '../data/canadaCities';

const TimezoneSelectionModal = ({ isOpen, onSave, userName }) => {
  const [cityOptions, setCityOptions] = useState([]);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const options = await getCityOptions();
        setCityOptions(options);
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };
    if (isOpen) {
      fetchCities();
    }
  }, [isOpen]);

  const filteredCities = useMemo(() => {
    if (!citySearchQuery) return cityOptions.slice(0, 50);
    const query = citySearchQuery.toLowerCase();
    return cityOptions.filter(opt => 
      opt.city.toLowerCase().includes(query) ||
      opt.province.toLowerCase().includes(query)
    ).slice(0, 100);
  }, [cityOptions, citySearchQuery]);

  const handleCitySelect = (option) => {
    setSelectedCity(option);
    setCitySearchQuery('');
    setIsCityDropdownOpen(false);
  };

  const handleSave = async () => {
    if (!selectedCity) return;
    setLoading(true);
    try {
      await onSave({
        city: selectedCity.city,
        province: selectedCity.province,
        timezone: selectedCity.timezone,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCityDropdownOpen && !event.target.closest('.timezone-modal-dropdown')) {
        setIsCityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCityDropdownOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Set Your Timezone</h2>
          </div>
          <p className="text-red-100 text-sm">
            Welcome{userName ? `, ${userName}` : ''}! Please select your city to ensure accurate booking times.
          </p>
        </div>
        <div className="p-6">
          <div className="mb-6">
            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 mb-3">
              <Clock className="w-5 h-5 text-red-600" />
              <span className="font-medium">Why do we need this?</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
              Canada has 6 different time zones. Setting your timezone ensures that all booking times are displayed correctly in your local time.
            </p>
          </div>
          <div className="timezone-modal-dropdown mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-red-600" />
              Select Your City
            </label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  value={isCityDropdownOpen ? citySearchQuery : (selectedCity ? selectedCity.label : '')}
                  onChange={(e) => {
                    setCitySearchQuery(e.target.value);
                    setIsCityDropdownOpen(true);
                  }}
                  onFocus={() => setIsCityDropdownOpen(true)}
                  placeholder="Search for your city..."
                  className="pl-10 border-2 border-gray-200 dark:border-gray-600 focus:border-red-500 rounded-xl"
                />
              </div>
              {isCityDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {filteredCities.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {citySearchQuery ? 'No cities found' : 'Loading cities...'}
                    </div>
                  ) : (
                    filteredCities.map((option, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleCitySelect(option)}
                        className="w-full px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">{option.city}, {option.province}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{option.timezoneDisplay}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          {selectedCity && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-700">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-semibold text-green-800 dark:text-green-300">{selectedCity.city}, {selectedCity.province}</p>
                  <p className="text-sm text-green-600 dark:text-green-400">{selectedCity.timezoneDisplay}</p>
                </div>
              </div>
            </div>
          )}
          <Button
            onClick={handleSave}
            disabled={!selectedCity || loading}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-semibold disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save & Continue'}
          </Button>
          <p className="mt-4 text-xs text-center text-gray-500 dark:text-gray-400">
            You can change your timezone later in your profile settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TimezoneSelectionModal;
